import AuthStore from '@/stores/AuthStore';
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/nextjs';
import { observer } from 'mobx-react-lite';

import { BASE_USER_ROLES } from '@/common/constants/user';

import { Product } from '@/types/Content/Product/Product';
import { Variant } from '@/types/Content/Product/Variant';

import AuthModalStore from '@/stores/AuthModalStore';
import FridgeDoorStore from '@/stores/FridgeDoorStore';
import WishlistStore from '@/stores/WishlistStore';

import useAddToCart from '@/hooks/useAddToCart';

import { Button } from '@/components/Button/Button';
import { captureException } from '@sentry/nextjs';
import ProductAddForm from '@/widgets/product/ProductAddForm';
import { getSelectedOptions } from '@/domains/Wishlist/utils';
import { AffiliateButtons } from './AffiliateButtons';
import {
  ComissionTag,
  PurchaseSocialShare,
  SocialShareWrapper,
  Wrapper,
} from './PurchaseSection.styles';

interface PurchaseSectionProps
  extends Pick<
    Product,
    | 'id'
    | 'seo'
    | 'title'
    | 'affiliatedLinks'
    | 'shopifyId'
    | 'options'
    | 'type'
    | 'variants'
  > {
  currentVariant: Variant;
  commentCounter?: number;
  toCommentsClick?: () => void;
}

async function getVillage() {
  try {
    await FridgeDoorStore.fetchDefaultVillage();
  } catch (error) {
    Sentry.captureException(error);
  }
}

async function addToWishlist(
  productId,
  variantId,
  setIsWishlistLoading,
  villageSlug,
  isProductInWishlist,
  productType,
  wishlist,
) {
  setIsWishlistLoading(true);
  try {
    if (isProductInWishlist) {
      const toRemove = wishlist.products?.find(
        (product) => product.id === productId || product.shopifyId === productId,
      );
      await WishlistStore.removeProduct(
        toRemove.djangoId,
        villageSlug,
      );
    } else {
      await WishlistStore.addProduct(
        productId,
        variantId,
        productType,
        villageSlug,
      );
    }
  } catch (error) {
    captureException(error);
  } finally {
    setIsWishlistLoading(false);
  }
}

function PurchaseSectionComponent({
  commentCounter,
  toCommentsClick,
  ...props
}: PurchaseSectionProps): JSX.Element {
  const {
    id,
    seo,
    title,
    currentVariant,
    affiliatedLinks,
    shopifyId,
    options,
    variants,
    type: productType,
  } = props;
  const { currentVillage, isAdmin } = FridgeDoorStore;

  const [, addToCart] = useAddToCart();
  const [isGuest, setIsGuest] = useState(true);
  const [isWishlistLoading, setIsWishlistLoading] = useState();
  // eslint-disable-next-line prefer-destructuring
  const villageSlug = FridgeDoorStore.defaultVillage?.slug;
  const wishlist = WishlistStore.getWishlistByVillageId(villageSlug);
  const [isProductInWishlist, setIstProductInWishlist] = useState(false);

  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);

  const selectedAffiliates = useMemo(() => {
    if (!affiliatedLinks?.length) {
      return [];
    }
    const newAffiliates = affiliatedLinks.filter(
      (item) => item?.sku === selectedVariant?.sku,
    );
    return (newAffiliates?.length && newAffiliates) || [affiliatedLinks[0]];
  }, [affiliatedLinks, selectedVariant?.sku]);

  const initialValues = useMemo(() => {
    const values: Record<string, string> = {};
    options?.forEach((option) => {
      if (option.name && option.values[0]) {
        // eslint-disable-next-line prefer-destructuring
        values[option.name] = option.values[0];
      }
    });
    return values;
  }, [options]);

  const handleSubmit = useCallback(
    (values) => {
      const newVariant = variants.find((variant) => {
        return options?.every((option, i) => {
          if (values[option.name]) {
            return (
              (variant[`option${i + 1}`] ||
                (variant?.options && variant?.options[i])) ===
              values[option.name]
            );
          }
          return false;
        });
      });
      setSelectedVariant(newVariant || variants[0]);
    },
    [options, variants],
  );

  const handleClick: MouseEventHandler = (event) => {
    event.stopPropagation();
    if (AuthStore.isLoggedIn) {
      const variantId = selectedVariant?.id || selectedVariant?.key;
      addToCart(variantId, 1, [
        villageSlug && {
          key: '_village_slug',
          value: villageSlug,
        },
        {
          key: '_user_email',
          value: AuthStore.user?.email,
        },
        {
          key: '_variant_options',
          value: JSON.stringify(
            getSelectedOptions(
              options,
              variants?.find((variant) => variant?.id === variantId),
            ),
          ),
        },
      ]);
    } else {
      AuthModalStore.showModal('login');
      setIsGuest(false);
    }
  };

  useEffect(() => {
    if (!isGuest && !!AuthStore.user && !AuthStore.isLoadingUser) {
      const variantId = selectedVariant?.id || selectedVariant?.key;
      addToCart(variantId, 1, [
        {
          key: '_village_slug',
          value: villageSlug,
        },
        {
          key: '_user_email',
          value: AuthStore.user?.email,
        },
        {
          key: '_variant_options',
          value: JSON.stringify(
            getSelectedOptions(
              options,
              variants?.find((variant) => variant?.id === variantId),
            ),
          ),
        },
      ]);
    }
  }, [isGuest, AuthStore.isLoadingUser, villageSlug]);

  const { role } = currentVillage?.subscription_info || {};
  const isSupporterMember =
    !isAdmin && role?.label === BASE_USER_ROLES.SUPPORTER;
  useEffect(() => {
    if (villageSlug) {
      WishlistStore.fetchWishlist(villageSlug);
    }
  }, [villageSlug]);

  useEffect(() => {
    if (wishlist?.products?.length)
      setIstProductInWishlist(
        wishlist?.products
          ?.map(({ id }) => id)
          ?.filter((productId) => productId === shopifyId || productId === id)
          ?.length > 0,
      );
  }, [wishlist, shopifyId, id]);

  useEffect(() => {
    getVillage();
  }, []);

  useEffect(() => {
    handleSubmit(initialValues);
  }, [handleSubmit, initialValues]);

  return (
    <Wrapper>
      <ProductAddForm
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        loading={false}
        product={
          {
            id,
            seo,
            title,
            affiliatedLinks,
            shopifyId,
            options,
            variants,
            type: productType,
          } as Product
        }
      />
      {!isSupporterMember && (
        <Button
          onClick={() =>
            addToWishlist(
              shopifyId || id,
              currentVariant?.id || currentVariant?.key,
              setIsWishlistLoading,
              villageSlug,
              isProductInWishlist,
              productType,
              wishlist,
            )
          }
          loading={isWishlistLoading}
        >
          {isProductInWishlist ? 'Remove from ' : 'Add to '} WishList
        </Button>
      )}
      <AffiliateButtons
        affiliatedLinks={selectedAffiliates}
        currentVariant={selectedVariant}
        shopifyId={shopifyId}
        handleAddToCart={handleClick}
      />
      {affiliatedLinks && (
        <ComissionTag>OneVillage may earn a commission</ComissionTag>
      )}
      <SocialShareWrapper>
        Share on:
        <PurchaseSocialShare
          seo={seo}
          title={title}
          hideLabel
          commentCounter={commentCounter}
          toCommentsClick={toCommentsClick}
        />
      </SocialShareWrapper>
    </Wrapper>
  );
}

PurchaseSectionComponent.defaultProps = {
  commentCounter: undefined,
  toCommentsClick: () => undefined,
};

export const PurchaseSection = observer(PurchaseSectionComponent);
